import {AfterViewInit, Component, OnInit} from "@angular/core";
import {StepComponent} from "../../step/step.component";
import {Step, StepService} from "../../services/step.service";
import {ActivatedRoute, Router} from "@angular/router";
import {ElementService} from "../../services/element.service";
import {PortalService} from "../../services/portal.service";
import {PhotovoltaikcheckAnfrage} from "../anfrage/photovoltaikcheck.anfrage";
import {PhotovoltaikcheckRequestService} from "../services/request.service";
import {EingabeForm} from "./eingabe.form";
import {PhotovoltaikcheckConstants} from "../photovoltaikcheck.constants";
import {DecimalPipe} from "@angular/common";

/**
 * Overwrite the Generic step component for the first section fo the photovoltaikcheck.
 *
 * @inheritDoc
 */
@Component({
    selector: 'app-step-eingabe-pvc',
    templateUrl: '../../step/step.component.html',
    providers: [DecimalPipe],
    standalone: false
})

export class EingabeStepComponent extends StepComponent implements OnInit, AfterViewInit {
  /**
   * Path to the entry page.
   */
  readonly PATH_EINSTIEG = '../../../einstieg';
  readonly PATH_EINSTIEG_BALCONY = '../../../einstieg/balkon';

  /**
   * path to the result page.
   */
  readonly PATH_ERGEBNIS = '../../../ergebnis';

  /**
   * (input) data of the user
   */
  anfrage: PhotovoltaikcheckAnfrage;

  /**
   * @inheritDoc
   */
  formConfig: EingabeForm;

  /**
   * Overwrite the label prefix.
   */
  textkeyPrefix = PhotovoltaikcheckConstants.PHOTOVOLTAIKCHECK;

  /**
   * constructor
   *
   * @param stepService
   * @param router
   * @param requestService
   * @param elementService
   * @param portalService
   * @param route
   * @param decimapPipe
   */
  constructor(protected stepService: StepService, protected router: Router, protected requestService: PhotovoltaikcheckRequestService,
              protected elementService: ElementService, protected portalService: PortalService, protected route: ActivatedRoute,
              protected decimapPipe: DecimalPipe) {
    super(stepService, router, elementService);
  }

  /**
   * @inheritDoc
   */
  ngOnInit() {
    // add formconfig
    this.formConfig = new EingabeForm();
    super.ngOnInit();

    // get the current input data and preset it
    this.anfrage = this.requestService.getAnfrage();
    this.formConfig.anfrage = this.anfrage;
    this.formConfig.setData(this.anfrage.userInput);
    this.formConfig.updateFields(this.anfrage.userInput);

    // update the previous step
    this.onStepUpdate();

    // init error placeholders
    this.initPlaceholders();
  }

  /**
   * @inheritdoc
   */
  ngAfterViewInit() {
    super.ngAfterViewInit();

    // make sure to scroll upwards
    setTimeout(() => {
      this.elementService.scrollToElement(this.stepForm.nativeElement);
    }, 0);
  }

  /**
   * Return the next step from a step list
   */
  getNextStepFromList(stepList: Step[]): any {
    // set all data again in the form to avoid using old data for toggle fields
    if (this.anfrage?.userInput && this.formConfig) {
      this.formConfig.updateFields(this.anfrage.userInput);
      // save the data again. In some case some fields are auto updated
      this.saveData({});
    }

    return super.getNextStepFromList(stepList);
  }

  /**
   * @inheritDoc
   * no autosubmit on the first step
   */
  setAutoSubmitAndInlineHelp() {
    super.setAutoSubmitAndInlineHelp();

    // avoid autosubmit on step with roof input
    if(this.steps.currentStep.name === 'aufstellort/1') {
      this.autoSubmit = false;
    }
  }

  /**
   * @inheritDoc
   */
  saveData(data: { [key: string]: any }) {
    // merge the data with the existing data
    this.anfrage.userInput = {...this.anfrage.userInput, ...data};
    this.requestService.setAnfrage(this.anfrage);

    // save the data also to the server
    this.requestService.saveDataToServer();

    super.saveData(data);
  }

  /**
   * @inheritdoc
   */
  onStepUpdate() {
    super.onStepUpdate();

    // set previous step on first step to go back to 'einstieg' page
    if (this.previousStep === null) {
      this.previousStep = new Step('einstieg', this.anfrage?.path === PhotovoltaikcheckAnfrage.PATH_BALCONY ?
        this.PATH_EINSTIEG_BALCONY : this.PATH_EINSTIEG);
    }

    // set (dummy) next step on last step, if there is no next step
    if (this.steps.nextStep === null) {
      this.steps.nextStep = new Step('ergebnis', this.PATH_ERGEBNIS);
    }

    // extra helptext for the building type
    if(this.steps.currentStep.name === 'basisdaten/2' && this.anfrage?.interesse === PhotovoltaikcheckConstants.PATH_PVANLAGE) {
      this.helpTextkey += '.mfh';
    }

    // extra help text on some steps, depending on the path
    if(this.steps?.currentStep?.name === 'aufstellort/1' && this.anfrage?.interesse === PhotovoltaikcheckConstants.PATH_BALKONKRAFTWERK) {
      this.helpTextkey += '.balkonkraftwerk';
    }

    if(this.steps?.currentStep?.name === 'aufstellort/2' &&
      this.anfrage?.userInput.aufstellort === PhotovoltaikcheckConstants.AUFSTELLORT_PULTDACH) {
      this.helpTextkey += '.pultdach';
    }

    if(this.steps?.currentStep?.name === 'stromverbrauch/3' &&
      this.anfrage?.userInput.gebaeudetyp === PhotovoltaikcheckConstants.GEBAEUDETYP_MFH) {
      this.formConfig.labelPostfix = '.mfh';
    }
  }

  /**
   * @inheritdoc
   */
  onSubmit() {
    if(this.isAdvisorEnd()) {
      // save data
      this.saveData(this.formConfig.getData());

      // navigate to end page
      this.router.navigateByUrl(
        this.portalService.currentPortalId + "/photovoltaikcheck/eingabe/ausstieg",
        {state: {lastStep: this.router.url}}
      );
      return;
    }

    // check if we should show the result
    let calculateResult = (this.steps?.nextStep?.name === 'ergebnis' || this.steps?.nextStep?.name === 'ausstieg');
    const formData = this.formConfig.getData();
    if(this.steps.currentStep.name === 'aufstellort/2' && this.anfrage?.interesse === PhotovoltaikcheckConstants.PATH_BALKONKRAFTWERK) {
      calculateResult = true;
    }
    else if(this.steps.currentStep.name === 'aufstellort/1' &&
      this.anfrage?.interesse === PhotovoltaikcheckConstants.PATH_BALKONKRAFTWERK &&
      formData.aufstellort === PhotovoltaikcheckConstants.AUFSTELLORT_FREIFLAECHE) {
      calculateResult = true;
    }

    if (calculateResult && this.form.valid) {
      // reset some settings from earlier sessions
      this.anfrage.configurationSelected = null;
      this.anfrage.userInput.eigenkapital = null;
      this.anfrage.userInput.grundpreis_euro_monat = null;
      this.anfrage.userInput.arbeitspreis_cent_kwh = null;
      this.anfrage.userInput.verschattung = null;
      delete this.anfrage.userInput.eigennutzung_flaeche;
      delete this.anfrage.userInput.volleinspeisung_flaeche;
      delete this.anfrage.userInput.batteriespeicher_kapazitaet;
      this.saveData(this.formConfig.getData());

      // start loading animation
      this.loading = true;

      // calculate result and show last page
      const backendService = this.requestService.backendService;
      backendService.getRequestId().subscribe(requestId => {
        backendService.result(this.anfrage, requestId).subscribe(result => {
          // stop lading animation
          this.loading = false;

          // save result
          this.anfrage.result = result;
          this.requestService.setAnfrage(this.anfrage);

          // show result page
          this.router.navigate([this.PATH_ERGEBNIS], {
            relativeTo: this.route
          });
        });
      });

      return;
    }

    super.onSubmit();
  }

  /**
   * check if we need to show and end page for certain users.
   */
  isAdvisorEnd(): boolean {
    // no renter in multi apartement houses for big devices
    if (this.anfrage.interesse === PhotovoltaikcheckConstants.PATH_PVANLAGE &&
      this.anfrage.userInput?.nutzungstyp === PhotovoltaikcheckConstants.NUTZUNGSTYP_MIETER &&
      this.form.get("gebaeudetyp")?.value === PhotovoltaikcheckConstants.GEBAEUDETYP_MFH) {
      return true;
    }

    // no owner for small devices on multi apartement houses
    if (this.anfrage.interesse === PhotovoltaikcheckConstants.PATH_BALKONKRAFTWERK &&
      this.anfrage.userInput?.nutzungstyp === PhotovoltaikcheckConstants.NUTZUNGSTYP_HAUSEIGENTUEMER &&
      this.form.get("gebaeudetyp")?.value === PhotovoltaikcheckConstants.GEBAEUDETYP_MFH) {
      return true;
    }

    return false;
  }

  /**
   * initialize the placeholders for the step error.
   */
  initPlaceholders(): void {
    this.stepErrorPlaceholder = {
      consumptionMax: this.decimapPipe.transform(this.anfrage.getMaxConsumption(), '1.0-0')
    };
  }
}
